import { Form } from '@form/Form';
import { FormInput } from '@form/index';
import { useApi } from '@hooks/useApi';
import useCurrency from '@hooks/useCurrency';
import { CircularProgress } from '@ui';
import { objectToFormData } from 'helpers/api';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import { DragEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FieldValues, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button } from '~/components/ui';
import AccountFormInput from '../Account/AccountFormInput';
import { amountInputToCents, centsToAmountInput } from '../amountField';
import { isLedgerDecisionError, useDocumentMandant, useLedgerDecision } from '../hooks/useLedgerDecision';
import type { LedgerDecision } from '../LedgerDecisionDialog';
import { useTaxCodeOptions } from '../useTaxCodeOptions';

const PdfDocument = dynamic(
    async () => {
        // eslint-disable-next-line
        const module = await import('react-pdf');
        const workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).toString();
        module.pdfjs.GlobalWorkerOptions.workerSrc = workerSrc;
        return module.Document;
    },
    { ssr: false }
);

const PdfPage = dynamic(
    async () => {
        // eslint-disable-next-line
        const module = await import('react-pdf');
        return module.Page;
    },
    { ssr: false }
);

interface ExternalInvoiceData {
    id?: number | null;
    user_id?: number | null;
    due_date?: string | null;
    line0?: string | null;
    line1?: string | null;
    line2?: string | null;
    postcode?: string | null;
    city?: string | null;
    country?: string | null;
    remarks?: string | null;
    cost_1?: string | null;
    cost_2?: string | null;
    is_credit?: boolean | null;
    account_number?: string | null;
    taxes?: number | null;
    tax_code?: string | null;
    value?: number | null;
    currency?: string | null;
    iban?: string | null;
    bic?: string | null;
    debtor?: string | null;
    file_id?: number | null;
    document_date?: string | null;
    mandant_id?: number | null;
    ledger_decision?: LedgerDecision | null;
}

interface InvoiceExternalImportFormProps {
    clubSlug: string;
    data?: ExternalInvoiceData;
    externalInvoiceId?: number | null;
    previewUrl?: string;
    previewType?: string;
    allowFileUpload?: boolean;
    onSuccess?: (data: any) => void;
    onComplete?: () => void;
}

const InvoiceExternalImportUpload = ({
    onFilesSelected,
    isImporting
}: {
    onFilesSelected: (files: File[]) => void;
    isImporting: boolean;
}) => {
    const { t } = useTranslation('invoice');
    const inputRef = useRef<HTMLInputElement | null>(null);
    const dragCounterRef = useRef(0);
    const { register, watch, setValue } = useFormContext();
    const { ref, ...inputProps } = register('import_files');
    const files = watch('import_files') as FileList | undefined;
    const selectedFiles = Array.from(files ?? []);
    const [isDragging, setIsDragging] = useState(false);
    const label =
        selectedFiles.length === 0
            ? t('invoiceList.externalImport.selectFiles')
            : selectedFiles.length === 1
              ? selectedFiles[0]?.name
              // count is always >= 2 here (the === 1 case is handled above), so a flat plural string is fine
              : t('invoiceList.externalImport.filesSelected', { count: selectedFiles.length });

    const isAllowedFile = useCallback((file: File) => {
        if (file.type === 'application/pdf' || file.type.startsWith('image/')) return true;
        const lowerName = file.name.toLowerCase();
        return (
            lowerName.endsWith('.pdf') ||
            lowerName.endsWith('.png') ||
            lowerName.endsWith('.jpg') ||
            lowerName.endsWith('.jpeg') ||
            lowerName.endsWith('.webp')
        );
    }, []);

    const handleDragEnter = useCallback(
        (event: DragEvent<HTMLDivElement>) => {
            event.preventDefault();
            event.stopPropagation();
            if (isImporting) return;
            dragCounterRef.current += 1;
            setIsDragging(true);
        },
        [isImporting]
    );

    const handleDragLeave = useCallback((event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        dragCounterRef.current -= 1;
        if (dragCounterRef.current <= 0) {
            dragCounterRef.current = 0;
            setIsDragging(false);
        }
    }, []);

    const handleDragOver = useCallback((event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
    }, []);

    const handleDrop = useCallback(
        (event: DragEvent<HTMLDivElement>) => {
            event.preventDefault();
            event.stopPropagation();
            dragCounterRef.current = 0;
            setIsDragging(false);
            if (isImporting) return;
            const dropped = Array.from(event.dataTransfer.files ?? []);
            const allowed = dropped.filter(isAllowedFile);
            if (allowed.length > 0) {
                onFilesSelected(allowed);
            }
        },
        [isAllowedFile, isImporting, onFilesSelected]
    );

    useEffect(() => {
        if (!files || files.length === 0) return;
        onFilesSelected(Array.from(files));
        setValue('import_files', []);
        if (inputRef.current) {
            inputRef.current.value = '';
        }
    }, [files, onFilesSelected, setValue]);

    return (
        <div
            className={`tw-flex tw-flex-col tw-gap-6 tw-rounded-2xl tw-border-2 tw-border-dashed tw-p-10 tw-transition ${
                isDragging
                    ? 'tw-bg-primary-1/10 tw-border-primary tw-shadow-[0_0_0_2px_rgba(0,0,0,0.04)]'
                    : 'tw-border-accents-6'
            }`}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
        >
            <input
                {...inputProps}
                ref={(node) => {
                    ref(node);
                    inputRef.current = node;
                }}
                type="file"
                multiple
                accept="application/pdf,image/*"
                className="tw-hidden"
                disabled={isImporting}
            />
            <Button className="tw-w-full" onPress={() => inputRef.current?.click()} disabled={isImporting}>
                {label}
            </Button>
            <div className="tw-text-center tw-text-xs tw-text-accents-7">
                {t('invoiceList.externalImport.dropzoneHint')}
            </div>
        </div>
    );
};

const InvoiceExternalImportForm = ({
    clubSlug,
    data,
    externalInvoiceId,
    previewUrl,
    previewType,
    allowFileUpload,
    onSuccess,
    onComplete
}: InvoiceExternalImportFormProps) => {
    const { t } = useTranslation('invoice');
    const api = useApi();
    const { currencies } = useCurrency();
    const taxCodeOptions = useTaxCodeOptions();
    const [isLoading, setIsLoading] = useState(false);
    const [isImporting, setIsImporting] = useState(false);
    const [isPrefetching, setIsPrefetching] = useState(false);
    const [importError, setImportError] = useState<string | null>(null);
    const [currentData, setCurrentData] = useState<ExternalInvoiceData>(data ?? {});
    const ledgerDecision = useLedgerDecision(useDocumentMandant(clubSlug, currentData?.mandant_id));
    const [pendingFiles, setPendingFiles] = useState<File[]>([]);
    const [selectedUploadFile, setSelectedUploadFile] = useState<File | null>(null);
    const [prefetched, setPrefetched] = useState<{ file: File; data: ExternalInvoiceData } | null>(null);
    // eslint-disable-next-line
    const [numPages, setNumPages] = useState(0);
    const [pdfWidth, setPdfWidth] = useState<number | null>(null);
    // eslint-disable-next-line
    const [pdfHeight, setPdfHeight] = useState<number | null>(null);
    const [pageSize, setPageSize] = useState<{ width: number; height: number } | null>(null);
    const [zoom, setZoom] = useState(1);
    const previewContainerRef = useRef<HTMLDivElement | null>(null);
    const pdfOptions = useMemo(
        () => ({
            withCredentials: true,
            disableWorker: true,
            disableRange: true,
            disableStream: true
        }),
        []
    );

    const [resolvedFileUrl, setResolvedFileUrl] = useState<string | undefined>(previewUrl);
    const [resolvedPreviewType, setResolvedPreviewType] = useState<string | undefined>(previewType);
    const [resolvedPdfFile, setResolvedPdfFile] = useState<string | Blob | undefined>(previewUrl);
    const localPreviewUrlRef = useRef<string | null>(null);

    const apiHost = useMemo(() => {
        const host = process.env.NEXT_PUBLIC_API_HOST;
        if (!host) return undefined;
        return host.endsWith('/') ? host : `${host}/`;
    }, []);
    const fileUrl = resolvedFileUrl;

    useEffect(() => {
        return () => {
            if (previewUrl?.startsWith('blob:')) {
                URL.revokeObjectURL(previewUrl);
            }
            if (localPreviewUrlRef.current) {
                URL.revokeObjectURL(localPreviewUrlRef.current);
            }
        };
    }, [previewUrl]);

    useEffect(() => {
        setResolvedFileUrl(previewUrl);
        setResolvedPreviewType(previewType);
        setResolvedPdfFile(previewUrl);
    }, [previewType, previewUrl]);

    useEffect(() => {
        if (previewUrl || !currentData?.file_id) return;
        const controller = new AbortController();
        let objectUrl: string | undefined;
        const url = `${apiHost}file/${currentData.file_id}`;

        const fetchFile = async () => {
            try {
                const response = await fetch(url, { credentials: 'include', signal: controller.signal });
                if (!response.ok) return;
                const blob = await response.blob();
                objectUrl = URL.createObjectURL(blob);
                setResolvedFileUrl(objectUrl);
                setResolvedPreviewType(blob.type || undefined);
                setResolvedPdfFile(blob);
            } catch (error: any) {
                if (error?.name !== 'AbortError') {
                    console.error('File fetch error', error);
                }
            }
        };

        void fetchFile();

        return () => {
            controller.abort();
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
            }
        };
    }, [apiHost, currentData?.file_id, previewUrl]);

    useEffect(() => {
        if (data) {
            setCurrentData(data);
        }
    }, [data]);

    const isEditMode = Boolean(externalInvoiceId ?? currentData?.id);
    const canUploadFile = Boolean(allowFileUpload || isEditMode);

    const setPreviewFromFile = useCallback((file: File) => {
        const objectUrl = URL.createObjectURL(file);
        if (localPreviewUrlRef.current) {
            URL.revokeObjectURL(localPreviewUrlRef.current);
        }
        localPreviewUrlRef.current = objectUrl;
        setResolvedFileUrl(objectUrl);
        setResolvedPreviewType(file.type || undefined);
        setResolvedPdfFile(file);
    }, []);

    const enqueueFiles = useCallback(
        (files: File[]) => {
            if (!files?.length) return;
            setImportError(null);

            if (isEditMode) {
                const [nextFile] = files;
                if (!nextFile) return;
                setSelectedUploadFile(nextFile);
                setPrefetched(null);
                setPreviewFromFile(nextFile);
                return;
            }

            setSelectedUploadFile(null);
            setPendingFiles((prev) => [...prev, ...files]);
        },
        [isEditMode, setPreviewFromFile]
    );

    const fetchImport = useCallback(
        (file: File) =>
            new Promise<ExternalInvoiceData | null>((resolve) => {
                const apiHost = process.env.NEXT_PUBLIC_API_HOST?.replace(/\/api\/?$/, '/');
                const url = apiHost
                    ? `${apiHost}ai/invoice-external/${clubSlug}/import`
                    : `ai/invoice-external/${clubSlug}/import`;

                api({
                    url,
                    method: 'POST',
                    data: objectToFormData({ file }),
                    onSuccess: (response) => {
                        const imported = response?.data ?? response;
                        resolve(imported ?? {});
                    },
                    onFailure: () => resolve(null),
                    onError: () => resolve(null)
                });
            }),
        [api, clubSlug]
    );

    const importAsCurrent = useCallback(
        async (file: File, showOverlay: boolean) => {
            if (!file) return;
            setImportError(null);
            if (showOverlay) {
                setIsImporting(true);
            } else {
                setIsPrefetching(true);
            }
            setPreviewFromFile(file);
            const imported = await fetchImport(file);
            if (imported) {
                setCurrentData(imported);
            } else {
                setImportError(t('invoiceList.externalImport.importFailed'));
            }
            setIsImporting(false);
            setIsPrefetching(false);
        },
        [fetchImport, setPreviewFromFile, t]
    );

    const prefetchFile = useCallback(
        async (file: File) => {
            if (!file) return;
            setIsPrefetching(true);
            const imported = await fetchImport(file);
            if (imported) {
                setPrefetched({ file, data: imported });
            } else {
                setImportError(t('invoiceList.externalImport.importFailed'));
            }
            setIsPrefetching(false);
        },
        [fetchImport, t]
    );

    useEffect(() => {
        if (isEditMode || !allowFileUpload || isImporting || isPrefetching || pendingFiles.length === 0) return;
        if (resolvedFileUrl) return;
        const [nextFile, ...rest] = pendingFiles;
        setPendingFiles(rest);
        void importAsCurrent(nextFile, true);
    }, [allowFileUpload, importAsCurrent, isEditMode, isImporting, isPrefetching, pendingFiles, resolvedFileUrl]);

    useEffect(() => {
        if (isEditMode || !allowFileUpload || isPrefetching || prefetched || pendingFiles.length === 0) return;
        if (!resolvedFileUrl) return;
        const [nextFile, ...rest] = pendingFiles;
        setPendingFiles(rest);
        void prefetchFile(nextFile);
    }, [allowFileUpload, isEditMode, isPrefetching, pendingFiles, prefetched, prefetchFile, resolvedFileUrl]);

    const pdfFile = useMemo(() => {
        if (!resolvedPdfFile) return undefined;
        if (typeof resolvedPdfFile === 'string') {
            if (resolvedPdfFile.startsWith('blob:') || resolvedPdfFile.startsWith('data:')) {
                return resolvedPdfFile;
            }
            return { url: resolvedPdfFile, withCredentials: true };
        }
        return resolvedPdfFile;
    }, [resolvedPdfFile]);

    useEffect(() => {
        const container = previewContainerRef.current;
        if (!container) return;

        const updateSize = () => {
            const rect = container.getBoundingClientRect();
            const styles = window.getComputedStyle(container);
            const paddingLeft = Number.parseFloat(styles.paddingLeft || '0');
            const paddingRight = Number.parseFloat(styles.paddingRight || '0');
            const paddingTop = Number.parseFloat(styles.paddingTop || '0');
            const paddingBottom = Number.parseFloat(styles.paddingBottom || '0');
            const nextWidth = Math.floor(rect.width - paddingLeft - paddingRight);
            const nextHeight = Math.floor(rect.height - paddingTop - paddingBottom);
            setPdfWidth(nextWidth > 0 ? nextWidth : null);
            setPdfHeight(nextHeight > 0 ? nextHeight : null);
        };

        updateSize();

        if (typeof ResizeObserver === 'undefined') {
            window.addEventListener('resize', updateSize);
            return () => window.removeEventListener('resize', updateSize);
        }

        const observer = new ResizeObserver(updateSize);
        observer.observe(container);

        return () => observer.disconnect();
    }, []);

    const handleSubmit = useCallback(
        async (formData: FieldValues) => {
            // A document that already carries a decision keeps it; the server falls back to it.
            const decision = currentData?.ledger_decision
                ? undefined
                : await ledgerDecision.resolve(formData?.document_date ?? formData?.due_date);
            if (decision === null) return;

            const { user_invoice_account_number, ...restFormData } = formData ?? {};
            const valueInCents = amountInputToCents(formData?.value);
            const remarks = typeof formData?.remarks === 'string' ? formData.remarks.slice(0, 255) : formData?.remarks;
            const resolvedInvoiceId = externalInvoiceId ?? currentData?.id ?? null;
            const payload = {
                ...restFormData,
                account_number: user_invoice_account_number ?? formData?.account_number ?? null,
                is_credit: formData?.is_credit ? 1 : 0,
                tax_code: formData?.tax_code || null,
                remarks,
                value: valueInCents
            };
            const send = (ledger_decision?: LedgerDecision) => {
                const body = ledger_decision ? { ...payload, ledger_decision } : payload;
                setIsLoading(true);

                return api({
                    url: resolvedInvoiceId
                        ? `invoice-external/${clubSlug}/${resolvedInvoiceId}`
                        : `invoice-external/${clubSlug}/store`,
                    method: resolvedInvoiceId ? 'PUT' : 'POST',
                    data: selectedUploadFile ? objectToFormData({ ...body, file: selectedUploadFile }) : body,
                    muteWhen: (response) => !ledger_decision && isLedgerDecisionError(response),
                    onFailure: (response) => {
                        setIsLoading(false);
                        if (!ledger_decision) {
                            void ledgerDecision.retry(response).then((retried) => retried && send(retried));
                        }
                    },
                    onSuccess: ({ data }) => {
                        setIsLoading(false);
                        setSelectedUploadFile(null);
                        onSuccess?.(data);
                        if (allowFileUpload) {
                            if (prefetched) {
                                setPrefetched(null);
                                setCurrentData(prefetched.data);
                                setPreviewFromFile(prefetched.file);
                            } else if (pendingFiles.length > 0) {
                                const [nextFile, ...rest] = pendingFiles;
                                setPendingFiles(rest);
                                void importAsCurrent(nextFile, false);
                            } else {
                                onComplete?.();
                            }
                        }
                    }
                });
            };

            return send(decision);
        },
        [
            allowFileUpload,
            api,
            clubSlug,
            currentData?.id,
            currentData?.ledger_decision,
            externalInvoiceId,
            ledgerDecision,
            importAsCurrent,
            onComplete,
            onSuccess,
            pendingFiles,
            prefetched,
            selectedUploadFile,
            setPreviewFromFile
        ]
    );

    const isImagePreview = resolvedPreviewType?.startsWith('image/');
    const isBlobUrl = fileUrl?.startsWith('blob:') || fileUrl?.startsWith('data:');
    const pageScale = useMemo(() => {
        if (!pageSize || !pdfWidth) return undefined;
        return (pdfWidth / pageSize.width) * zoom;
    }, [pageSize, pdfWidth, zoom]);

    return (
        <Form
            onSubmit={handleSubmit}
            defaultValues={{
                ...currentData,
                is_credit: currentData?.is_credit ?? false,
                user_invoice_account_number: currentData?.account_number ?? null,
                value: centsToAmountInput(currentData?.value)
            }}
            className="tw-relative tw-flex tw-max-h-[85vh] tw-flex-col"
            aria-busy={isImporting}
        >
            {isImporting && (
                <div className="tw-absolute tw-inset-0 tw-z-10 tw-flex tw-items-center tw-justify-center tw-bg-white/70">
                    <div className="tw-flex tw-flex-col tw-items-center tw-gap-3">
                        <CircularProgress spin />
                        <div className="tw-text-sm tw-text-accents-7">{t('invoiceList.externalImport.processing')}</div>
                    </div>
                </div>
            )}
            <div className="tw-grid tw-min-h-0 tw-flex-1 tw-gap-4 lg:tw-grid-cols-2">
                <div className="tw-flex tw-min-h-0 tw-flex-col tw-gap-4">
                    <div
                        ref={previewContainerRef}
                        className="tw-flex tw-h-full tw-min-h-0 tw-items-start tw-justify-center tw-overflow-x-auto tw-overflow-y-auto tw-rounded-2xl tw-border tw-border-accents-2 tw-bg-accents-1 tw-p-4"
                    >
                        {fileUrl ? (
                            isImagePreview ? (
                                <div className="tw-flex tw-h-full tw-w-full tw-flex-col">
                                    {fileUrl ? (
                                        isBlobUrl ? (
                                            <img
                                                src={fileUrl}
                                                alt={t('invoiceList.externalImport.importedInvoiceAlt')}
                                                className="tw-h-full tw-w-full tw-object-contain"
                                            />
                                        ) : (
                                            <Image
                                                src={fileUrl}
                                                alt={t('invoiceList.externalImport.importedInvoiceAlt')}
                                                fill
                                                className="tw-object-contain"
                                                sizes="(max-width: 768px) 100vw, 50vw"
                                            />
                                        )
                                    ) : null}
                                </div>
                            ) : (
                                <div className="tw-flex tw-h-full tw-w-full tw-flex-col">
                                    <div className="tw-mb-3 tw-flex tw-items-center tw-justify-end tw-gap-2">
                                        <Button
                                            color="secondary"
                                            onClick={() =>
                                                setZoom((prev) => Math.max(0.6, Number((prev - 0.1).toFixed(2))))
                                            }
                                        >
                                            –
                                        </Button>
                                        <div className="tw-min-w-[64px] tw-text-center tw-text-xs tw-text-accents-6">
                                            {Math.round(zoom * 100)}%
                                        </div>
                                        <Button
                                            color="secondary"
                                            onClick={() =>
                                                setZoom((prev) => Math.min(2, Number((prev + 0.1).toFixed(2))))
                                            }
                                        >
                                            +
                                        </Button>
                                    </div>
                                    <div className="tw-min-h-0 tw-flex-1 tw-overflow-auto">
                                        <PdfDocument
                                            file={pdfFile}
                                            loading={
                                                <div className="tw-py-6 tw-text-center tw-text-sm tw-text-accents-6">
                                                    {t('invoiceList.externalImport.pdfLoading')}
                                                </div>
                                            }
                                            error={
                                                <div className="tw-text-danger-1 tw-py-6 tw-text-center tw-text-sm">
                                                    {t('invoiceList.externalImport.pdfLoadError')}
                                                </div>
                                            }
                                            onLoadSuccess={({ numPages: loadedPages }) => setNumPages(loadedPages)}
                                            onLoadError={(error) => {
                                                console.error('PDF load error', error);
                                                setNumPages(0);
                                            }}
                                            options={pdfOptions}
                                        >
                                            <PdfPage
                                                pageNumber={1}
                                                scale={pageScale ?? 1}
                                                className="tw-max-w-full"
                                                onLoadSuccess={(page) => {
                                                    const viewport = page.getViewport({ scale: 1 });
                                                    setPageSize({ width: viewport.width, height: viewport.height });
                                                }}
                                                renderTextLayer={false}
                                                renderAnnotationLayer={false}
                                            />
                                        </PdfDocument>
                                    </div>
                                </div>
                            )
                        ) : (
                            <>
                                {canUploadFile && (
                                    <div className="tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center">
                                        <div className="tw-w-full tw-max-w-xl">
                                            <div className="tw-mb-2 tw-text-center tw-text-xs tw-font-medium tw-text-accents-7">
                                                {t('invoiceList.externalImport.fileOptional')}
                                            </div>
                                            <InvoiceExternalImportUpload
                                                onFilesSelected={enqueueFiles}
                                                isImporting={isImporting}
                                            />
                                            {importError && (
                                                <div className="tw-text-danger-1 tw-mt-2 tw-text-sm">{importError}</div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
                <div className="tw-min-h-0 tw-overflow-auto">
                    <div className="tw-grid tw-gap-2 md:tw-grid-cols-2">
                        <FormInput name="debtor" label={t('invoiceList.externalImport.fields.debtor')} />
                    </div>
                    <div className="tw-grid tw-gap-2 md:tw-grid-cols-2">
                        <FormInput name="line0" label={t('invoiceList.externalImport.fields.nameCompany')} />
                        <FormInput name="line1" label={t('invoiceList.externalImport.fields.addressLine2')} />
                        <FormInput name="line2" label={t('invoiceList.externalImport.fields.addressLine3')} />
                        <FormInput name="postcode" label={t('invoiceList.externalImport.fields.postcode')} />
                        <FormInput name="city" label={t('invoiceList.externalImport.fields.city')} />
                        <FormInput name="country" label={t('invoiceList.externalImport.fields.country')} />
                    </div>
                    <div className="tw-grid tw-gap-2 md:tw-grid-cols-2">
                        <FormInput.DatePicker name="due_date" label={t('invoiceList.externalImport.fields.dueDate')} />
                        <FormInput.Checkbox
                            className="tw-flex tw-items-end"
                            name="is_credit"
                            label={t('invoiceList.externalImport.fields.isCredit')}
                        />
                    </div>
                    <div className="tw-grid tw-gap-2 md:tw-grid-cols-2">
                        <FormInput name="iban" label={t('invoiceList.externalImport.fields.iban')} />
                        <FormInput name="bic" label={t('invoiceList.externalImport.fields.bic')} />
                    </div>
                    <div className="tw-grid tw-gap-2 md:tw-grid-cols-2">
                        <AccountFormInput
                            name="user_invoice_account_number"
                            label={t('invoiceList.externalImport.fields.account')}
                            labelRight="SK"
                        />
                        <FormInput
                            name="taxes"
                            label={t('invoiceList.externalImport.fields.taxes')}
                            type="number"
                            min={0}
                            step={1}
                        />
                    </div>
                    <FormInput.Select
                        name="tax_code"
                        label={t('invoiceList.externalImport.fields.taxCode')}
                        options={taxCodeOptions}
                    />
                    <div className="tw-grid tw-gap-2 md:tw-grid-cols-2">
                        <FormInput
                            name="value"
                            label={t('invoiceList.externalImport.fields.value')}
                            type="number"
                            min={0}
                            step={0.01}
                        />
                        <FormInput.Select
                            name="currency"
                            label={t('invoiceList.externalImport.fields.currency')}
                            disableCustomValue
                            options={currencies.map((currency) => ({
                                key: currency.code,
                                label: currency.name
                            }))}
                        />
                    </div>
                    <div className="tw-grid tw-gap-2 md:tw-grid-cols-2">
                        <FormInput name="cost_1" label={t('invoiceList.externalImport.fields.costCenter1')} />
                        <FormInput name="cost_2" label={t('invoiceList.externalImport.fields.costCenter2')} />
                    </div>
                    <FormInput.Textarea name="remarks" label={t('invoiceList.externalImport.fields.remarks')} />
                </div>
            </div>
            <div className="tw-mt-6">
                <FormInput.Submit isLoading={isLoading || isImporting}>
                    {isEditMode
                        ? t('invoiceList.externalImport.editInvoice')
                        : t('invoiceList.externalImport.importInvoice')}
                </FormInput.Submit>
            </div>
        </Form>
    );
};

export default InvoiceExternalImportForm;
